import { v4 as uuid } from 'uuid';
import flatten from 'lodash/flatten';

import {
    EntryTypeSpecialtyCreationForm,
    EntryTypeSpecialtyData,
    EntryTypeSpecialtyUpdateForm,
} from 'chaika-frontend-types/frontend-cms';

import { EntryTypeSpecialtyApi } from '@api/EntryTypeSpecialtyApi';
import { store } from '@store/index';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';

interface EntryTypeSpecialityFindParams {
    specialtyIds: string[];
}

class EntryTypeSpecialtyService {
    public async find({ specialtyIds }: EntryTypeSpecialityFindParams): Promise<EntryTypeSpecialtyData[]> {
        const chunkedIds = getUniqChunkedIds(specialtyIds, 10);

        const responses = await Promise.all(
            chunkedIds.map(ids => EntryTypeSpecialtyApi.find({ specialtyId: ids.join() })),
        );

        const entryTypeSpecialties = flatten(responses.map(res => res.entryTypeSpecialties));

        store.entryTypeSpecialityList.addIfNotExist(entryTypeSpecialties);

        return entryTypeSpecialties;
    }

    public async getBySpecialtyId(specialtyId: string): Promise<EntryTypeSpecialtyData> {
        const { entryTypeSpecialties } = await EntryTypeSpecialtyApi.find({ specialtyId });

        store.entryTypeSpecialityList.addIfNotExist(entryTypeSpecialties);

        return entryTypeSpecialties?.[0];
    }

    public async getById(id: string): Promise<EntryTypeSpecialtyData> {
        const entryTypeSpeciality = await EntryTypeSpecialtyApi.getById(id);

        store.entryTypeSpecialityList.addIfNotExist([entryTypeSpeciality]);

        return entryTypeSpeciality;
    }

    public async createRelation(params: Omit<EntryTypeSpecialtyCreationForm, 'id'>): Promise<void> {
        const id = uuid();

        await EntryTypeSpecialtyApi.create({ id, ...params });
        const entryTypeSpeciality = await EntryTypeSpecialtyApi.getById(id);

        store.entryTypeSpecialityList.addIfNotExist([entryTypeSpeciality]);
    }

    public async updateRelation(id: string, params: EntryTypeSpecialtyUpdateForm): Promise<void> {
        await EntryTypeSpecialtyApi.update(id, params);

        const entryTypeSpeciality = store.entryTypeSpecialityList.getExisting(id);

        store.entryTypeSpecialityList.update(id, { ...entryTypeSpeciality, ...params });
    }

    public async deleteRelation(id: string): Promise<void> {
        await EntryTypeSpecialtyApi.delete(id);

        store.entryTypeSpecialityList.remove(id);
    }
}

export const entryTypeSpecialtyService = new EntryTypeSpecialtyService();
